import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../common/basic/QcsButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import styled from '@emotion/styled';

//One grid holding both layouts. The mobile one stacks buttons above and below the content, the
//desktop one puts them left and right of it, so the slots are named instead of relying on source
//order. Only one variant of each button group is displayed at a time and a display:none grid item
//takes up no space, so the two never collide in the same area.
const Layout = styled.div<{ mobileGap?: boolean }>(({ theme, mobileGap }) => ({
  display: 'grid',
  width: '100%',
  height: '100%',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'max-content auto max-content',
  gridTemplateAreas: '"lead" "content" "trail"',
  gap: mobileGap ? '1rem' : undefined,

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'max-content auto max-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: '"lead content trail"',
    gap: '0.5rem',
  },
}));

const MobileButtons = styled.div<{ area: 'lead' | 'trail' }>(({ theme, area }) => ({
  gridArea: area,
  display: 'grid',
  gap: '1rem',

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const DesktopButtons = styled.div<{ area: 'lead' | 'trail' }>(({ theme, area }) => ({
  gridArea: area,
  display: 'none',
  alignItems: 'center',
  position: 'relative',

  [theme.breakpoints.up('md')]: {
    display: 'grid',
  },
}));

//The children are mounted once and shared by both layouts. Rendering them separately per layout
//would mount the whole slide twice - duplicating its effects and requests, and giving screens like
//the signature pad two independent copies, so crossing the breakpoint swapped in an empty one.
const ContentWrapper = styled.div(() => ({
  gridArea: 'content',
  width: '100%',
  maxWidth: '90vw',
  margin: '0 auto',
}));

interface Props {
  backOnClick?: () => void;
  backOnClickMobile?: () => void;
  backDisabled?: boolean;
  onAbort?: () => void;
  nextOnClick?: () => void;
  nextDisabled?: boolean;
  mobileGap?: boolean;
  children: React.ReactNode;
}

export const TourniquetButtonContent: FC<Props> = ({
  backOnClick,
  backOnClickMobile,
  backDisabled,
  onAbort,
  nextOnClick,
  nextDisabled,
  mobileGap,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <Layout mobileGap={mobileGap}>
        <MobileButtons area="lead">
          <QcsButton variant="outlined" color="error" onClick={onAbort} sx={{ display: onAbort ? undefined : 'none' }}>
            {t('common.abort')}
          </QcsButton>

          <QcsButton
            variant="outlined"
            onClick={backOnClick || backOnClickMobile}
            disabled={backDisabled}
            sx={{
              display: backOnClick || backOnClickMobile ? undefined : 'none',
            }}
          >
            <ArrowBack />
            {t('common.back')}
          </QcsButton>
        </MobileButtons>

        <DesktopButtons area="lead">
          <QcsButton variant="contained" onClick={backOnClick} disabled={backDisabled} sx={{ visibility: backOnClick ? undefined : 'hidden' }}>
            <ArrowBack />
            {t('common.back')}
          </QcsButton>
        </DesktopButtons>

        <ContentWrapper>{children}</ContentWrapper>

        <MobileButtons area="trail">
          <QcsButton variant="contained" onClick={nextOnClick} disabled={nextDisabled} sx={{ display: nextOnClick ? undefined : 'none' }}>
            {t('common.nextStep')}
            <ArrowForward />
          </QcsButton>
        </MobileButtons>

        <DesktopButtons area="trail">
          <QcsButton
            variant="outlined"
            color="error"
            onClick={onAbort}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              visibility: onAbort ? undefined : 'hidden',
            }}
          >
            {t('common.abort')}
          </QcsButton>
          <QcsButton variant="contained" onClick={nextOnClick} disabled={nextDisabled} sx={{ visibility: nextOnClick ? undefined : 'hidden' }}>
            {t('common.nextStep')}
            <ArrowForward />
          </QcsButton>
        </DesktopButtons>
      </Layout>
    </div>
  );
};
